import React, { ReactNode } from "react";
import { BookHalf, Sun, Moon } from "react-bootstrap-icons";
import { Button, Col, Container, Row } from "react-bootstrap";

import MetricCards, { MetricCardItem } from "../../../components/analytics/MetricCards";
import { DocsSidebar, WidgetCard } from "../../../components/analytics";
import { DocsSidebarEntry, WidgetTheme } from "../../../types/analytics";
import { getWidgetTheme, getDefaultDateRange } from "../../../utils/analytics";
import { getAnalyticsWidget } from "../../../utils/Requests";

type ExternalLandingProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type ExternalLandingState = {
    isLoading: boolean;
    error: string | null;
    widgetStates: Record<string, { isLoading: boolean; error: string | null }>;
    isDarkTheme: boolean;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
    topLineMetrics: MetricCardItem[];
    recsMetrics: MetricCardItem[];
    kpiDescription: string | null;
    recsDescription: string | null;
};

class ExternalLanding extends React.Component<ExternalLandingProps, ExternalLandingState> {
    private activeRequestId = 0;
    private isMountedFlag = false;

    private getSchemaDescription = (schema: Record<string, { description?: string }> | undefined, keys: string[]): string | undefined => {
        for (const key of keys) {
            const description = schema?.[key]?.description;
            if (description) {
                return description;
            }
        }
        return undefined;
    };

    constructor(props: ExternalLandingProps) {
        super(props);
        this.state = {
            isLoading: true,
            error: null,
            /* eslint-disable camelcase */
            widgetStates: {
                kpi_traffic: { isLoading: true, error: null },
                kpi_user_kpi: { isLoading: true, error: null },
                recs_plays: { isLoading: true, error: null }
            },
            /* eslint-enable camelcase */
            isDarkTheme: true,
            sidebarOpen: false,
            sidebarEntries: [],
            topLineMetrics: [],
            recsMetrics: [],
            kpiDescription: null,
            recsDescription: null
        };
    }

    private toggleSidebar = (): void => {
        this.setState((prevState) => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    public componentDidMount(): void {
        this.isMountedFlag = true;
        this.fetchTopLineMetrics();
    }

    public componentWillUnmount(): void {
        this.isMountedFlag = false;
        this.activeRequestId += 1;
    }

    private buildSidebarEntry = (widgetKey: string, response: unknown): DocsSidebarEntry | null => {
        const meta = (response as any)?.widget;
        if (!meta) {
            return null;
        }

        return {
            widgetKey,
            displayName: meta.display_name,
            description: meta.description,
            schema: (response as any)?.schema
        };
    };

    private upsertSidebarEntry = (entries: DocsSidebarEntry[], entry: DocsSidebarEntry): DocsSidebarEntry[] => {
        const nextEntries = entries.filter(current => current.widgetKey !== entry.widgetKey);
        nextEntries.push(entry);
        return nextEntries;
    };

    private applyWidgetResponse = (
        requestId: number,
        widgetKey: string,
        loadResponse: () => Promise<unknown>,
        onSuccess: (response: unknown, prevState: ExternalLandingState) => Partial<ExternalLandingState>
    ): void => {
        void (async () => {
            try {
                const response = await loadResponse();
                if (!this.isMountedFlag || requestId !== this.activeRequestId) {
                    return;
                }

                const sidebarEntry = this.buildSidebarEntry(widgetKey, response);
                this.setState(prevState => {
                    const widgetStates = {
                        ...prevState.widgetStates,
                        [widgetKey]: { isLoading: false, error: null }
                    };

                    return {
                        ...onSuccess(response, prevState),
                        sidebarEntries: sidebarEntry ? this.upsertSidebarEntry(prevState.sidebarEntries, sidebarEntry) : prevState.sidebarEntries,
                        widgetStates,
                        isLoading: Object.values(widgetStates).some((state) => state.isLoading)
                    } as ExternalLandingState;
                });
            } catch (_error) {
                if (!this.isMountedFlag || requestId !== this.activeRequestId) {
                    return;
                }

                this.setState(prevState => {
                    const widgetStates = {
                        ...prevState.widgetStates,
                        [widgetKey]: { isLoading: false, error: "Failed to load widget data" }
                    };

                    return {
                        widgetStates,
                        isLoading: Object.values(widgetStates).some((state) => state.isLoading)
                    } as ExternalLandingState;
                });
            }
        })();
    };

    private fetchTopLineMetrics = async (): Promise<void> => {
        const { startDate, endDate } = getDefaultDateRange();
        const requestId = ++this.activeRequestId;

        this.setState({
            isLoading: true,
            error: null,
            topLineMetrics: [],
            recsMetrics: [],
            kpiDescription: null,
            recsDescription: null,
            sidebarEntries: [],
            /* eslint-disable camelcase */
            widgetStates: {
                kpi_traffic: { isLoading: true, error: null },
                kpi_user_kpi: { isLoading: true, error: null },
                recs_plays: { isLoading: true, error: null }
            }
            /* eslint-enable camelcase */
        });

        this.applyWidgetResponse(requestId, "kpi_traffic", () => getAnalyticsWidget(this.props.customerConfig, "kpi_traffic", startDate, endDate, [], [], []), (response: unknown, prevState: ExternalLandingState) => {
            const trafficRow = Array.isArray((response as any)?.result) ? (response as any).result[0] : null;
            const trafficSchema = (response as any)?.schema as Record<string, { description?: string }> | undefined;
            return {
                topLineMetrics: [
                    {
                        key: "session_count",
                        label: "Sessions",
                        value: trafficRow?.session_count ?? null,
                        changePercent: trafficRow?.session_percent_change ?? null,
                        description: this.getSchemaDescription(trafficSchema, ["session_count"])
                    },
                    {
                        key: "play_count",
                        label: "Video Starts",
                        value: trafficRow?.play_count ?? null,
                        changePercent: trafficRow?.plays_percent_change ?? null,
                        description: this.getSchemaDescription(trafficSchema, ["play_count", "plays"])
                    },
                    ...prevState.topLineMetrics.filter(metric => !["session_count", "play_count"].includes(metric.key))
                ],
                kpiDescription: (response as any)?.widget?.description || prevState.kpiDescription
            };
        });

        this.applyWidgetResponse(requestId, "kpi_user_kpi", () => getAnalyticsWidget(this.props.customerConfig, "kpi_user_kpi", startDate, endDate, [], [], []), (response: unknown, prevState: ExternalLandingState) => {
            const userKpiRow = Array.isArray((response as any)?.result) ? (response as any).result[0] : null;
            const userKpiSchema = (response as any)?.schema as Record<string, { description?: string }> | undefined;
            return {
                topLineMetrics: [
                    ...prevState.topLineMetrics.filter(metric => !["user_count", "playing_user_count", "viewing_user_count", "device_count"].includes(metric.key)),
                    {
                        key: "user_count",
                        label: "Users",
                        value: userKpiRow?.user_count ?? null,
                        changePercent: userKpiRow?.user_percent_change ?? null,
                        description: this.getSchemaDescription(userKpiSchema, ["user_count", "users"])
                    },
                    {
                        key: "playing_user_count",
                        label: "Playing Users",
                        value: userKpiRow?.playing_user_count ?? null,
                        changePercent: userKpiRow?.playing_user_percent_change ?? null,
                        description: this.getSchemaDescription(userKpiSchema, ["playing_user_count"])
                    },
                    {
                        key: "viewing_user_count",
                        label: "Viewing Users",
                        value: userKpiRow?.viewing_user_count ?? null,
                        changePercent: userKpiRow?.viewing_user_percent_change ?? null,
                        description: this.getSchemaDescription(userKpiSchema, ["viewing_user_count"])
                    },
                    {
                        key: "device_count",
                        label: "Devices",
                        value: userKpiRow?.device_count ?? null,
                        changePercent: userKpiRow?.device_percent_change ?? null,
                        description: this.getSchemaDescription(userKpiSchema, ["device_count"])
                    }
                ]
            };
        });

        this.applyWidgetResponse(requestId, "recs_plays", () => getAnalyticsWidget(this.props.customerConfig, "recs_plays", startDate, endDate, [], [], []), (response: unknown, prevState: ExternalLandingState) => {
            const recsSchema = (response as any)?.schema as Record<string, { description?: string }> | undefined;
            const recsRowsRaw = Array.isArray((response as any)?.result) ? (response as any).result : [];
            const allRecsRows = recsRowsRaw
                .map((row: Record<string, any>) => {
                    const ruleId = String(row.parameters_rule_id || row.parameter_rule_id || "").trim();
                    const source = (!ruleId || ruleId.toLowerCase() === "unknown" || ruleId.toLowerCase() === "null")
                        ? "All recommendations"
                        : ruleId;
                    return {
                        source,
                        plays: Number(row.plays || 0),
                        users: Number(row.users || 0),
                        views: Number(row.views || row.view_count || 0)
                    };
                })
                .filter((row: Record<string, any>) => row.source === "All recommendations");

            const recTotals = allRecsRows.reduce((acc: { plays: number; users: number; views: number }, row: Record<string, any>) => {
                acc.plays += Number(row.plays || 0);
                acc.users += Number(row.users || 0);
                acc.views += Number(row.views || 0);
                return acc;
            }, { plays: 0, users: 0, views: 0 });

            return {
                recsMetrics: [
                    {
                        key: "recs_plays",
                        label: "Recommendation Plays",
                        value: recTotals.plays,
                        description: this.getSchemaDescription(recsSchema, ["plays", "rec_plays"])
                    },
                    {
                        key: "recs_users",
                        label: "Recommendation Users",
                        value: recTotals.users,
                        description: this.getSchemaDescription(recsSchema, ["users", "rec_users"])
                    },
                    {
                        key: "recs_views",
                        label: "Recommendation Views",
                        value: recTotals.views,
                        description: this.getSchemaDescription(recsSchema, ["views", "view_count", "rec_views"])
                    }
                ],
                recsDescription: (response as any)?.widget?.description || prevState.recsDescription
            };
        });
    };

    public render(): ReactNode {
        const { isDarkTheme, topLineMetrics, recsMetrics, error, kpiDescription, recsDescription, sidebarOpen, sidebarEntries, widgetStates } = this.state;
        const theme: WidgetTheme = getWidgetTheme(isDarkTheme);

        return (
            <>
                <div style={{ display: "flex", alignItems: "flex-start", backgroundColor: isDarkTheme ? "#060606" : "#ffffff", color: isDarkTheme ? "#ffffff" : "#000000", minHeight: "100vh" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Container className="mw-100" style={{ paddingBottom: "40px", position: "relative" }}>
                            <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                                <Button
                                    onClick={this.toggleTheme}
                                    style={{ backgroundColor: "transparent", border: "none", fontSize: "24px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                                    title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                                >
                                    {isDarkTheme ? <Sun /> : <Moon />}
                                </Button>
                                <Button
                                    onClick={this.toggleSidebar}
                                    style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                                    title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                                >
                                    <BookHalf />
                                </Button>
                            </div>
                            <Row style={{ marginBottom: "16px" }}>
                                <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                                    <h2 style={{ margin: 0, color: theme.text }}>Analytics - Overview</h2>
                                </Col>
                            </Row>
                            <WidgetCard
                                title="KPI over last 7 days"
                                titleAdornment={(
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        href="/analytics/external/kpi-dashboard"
                                    >
                                        Open KPI dashboard
                                    </Button>
                                )}
                                description={kpiDescription || undefined}
                                theme={theme}
                                isLoading={widgetStates.kpi_traffic.isLoading || widgetStates.kpi_user_kpi.isLoading}
                                error={widgetStates.kpi_traffic.error || widgetStates.kpi_user_kpi.error || error}
                                isEmpty={!(widgetStates.kpi_traffic.isLoading || widgetStates.kpi_user_kpi.isLoading) && !(widgetStates.kpi_traffic.error || widgetStates.kpi_user_kpi.error || error) && topLineMetrics.length === 0}
                                emptyMessage="No KPI top line data available."
                            >
                                <MetricCards
                                    metrics={topLineMetrics}
                                    theme={theme}
                                    emptyMessage="No KPI top line data available."
                                    minCardWidth="200px"
                                    maxCardWidth="220px"
                                    gridGap="16px"
                                />
                            </WidgetCard>
                            <WidgetCard
                                title="Recommendation performance"
                                titleAdornment={(
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        href="/analytics/external/recs-dashboard"
                                    >
                                        Open recs dashboard
                                    </Button>
                                )}
                                description={recsDescription || undefined}
                                theme={theme}
                                isLoading={widgetStates.recs_plays.isLoading}
                                error={widgetStates.recs_plays.error || error}
                                isEmpty={!widgetStates.recs_plays.isLoading && !(widgetStates.recs_plays.error || error) && recsMetrics.length === 0}
                                emptyMessage="No recommendation performance data available."
                            >
                                <MetricCards
                                    metrics={recsMetrics}
                                    theme={theme}
                                    emptyMessage="No recommendation performance data available."
                                    minCardWidth="220px"
                                    maxCardWidth="260px"
                                    gridGap="16px"
                                />
                            </WidgetCard>
                        </Container>
                    </div>
                    <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
                </div>

            </>
        );
    }
}

export default ExternalLanding;